/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
/* eslint-disable class-methods-use-this */
import {
  Directive, ElementRef, HostListener, Input,
} from '@angular/core';

/**
 * Replaces new line characters in a string.
 */
function replaceNewlines(text, replacementString: string = ''): string {
  return text.replace(/[\n\r]+/g, replacementString);
}

const allowedKeys = ['Backspace', 'Delete', 'Shift', 'Control', 'Alt', 'Meta'];

const allowedKeysWithCommand = ['a', 'c', 'v', 'x', 'z'];

/**
 * Provides supportive functionality to contenteditable elements to match
 * common input element behavior.
 * This includes:
 * - preventing newline characters by default
 * - enforces a max-length
 */
@Directive({
  selector: '[bulbContenteditableSupport]',
  standalone: false,
})
export class ContenteditableSupportDirective {

  @Input()
    maxlength?: number;

  constructor(
    private readonly elementRef: ElementRef,
  ) { }

  @HostListener('keydown.shift.enter', ['$event'])
  @HostListener('keydown.enter', ['$event'])
  private preventNewLines(e: KeyboardEvent) {
    e.preventDefault();
  }

  @HostListener('keydown', ['$event'])
  private preventMaxLength(e: KeyboardEvent) {
    const isShortcut = (e.ctrlKey || e.metaKey) && allowedKeysWithCommand.includes(e.key);
    const isAllowedKey = allowedKeys.includes(e.key);
    const isAllowedAction = isAllowedKey || isShortcut;

    if (isAllowedAction) {
      return;
    }

    const isTooLong = this.elementRef.nativeElement.innerText.length >= this.maxlength;
    if (isTooLong) {
      e.preventDefault();
    }
  }

  /**
   * Intercepts 'paste' event and transforms the pasted text.
   * All paste transforms need to occur in the same event
   * because there is no simple way to queue multiple transforms to
   * multiple paste event.
   */
  @HostListener('paste', ['$event'])
  private async handlePaste(e: ClipboardEvent) {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text/plain');
    const trimmedPastedContent = this.getTrimmedPastedContent(pastedText, this.maxlength);
    const sansNewLines = replaceNewlines(trimmedPastedContent);
    // NOTE: document.execCommand is deprecated, but there is no (simple) replacement
    // that handles inserting content and undo/redo at this time.
    document.execCommand('insertText', false, sansNewLines);
  }

  /**
   * Trims string down to a maximum length
   */
  private getTrimmedPastedContent(text: string, maxLength: number): string {
    const contentLength = this.elementRef.nativeElement.innerText.length;
    const allowedPasteLength = maxLength - contentLength;
    const slicedPaste = text.substring(0, allowedPasteLength);
    return slicedPaste;
  }
}
